import React from "react";
import { produce } from "immer";

import { logger } from "@jslib/shared/utils/logger";

import { useFlag } from "@/components/FeatureFlags/hooks";

import { type VerificationService } from "../types/types";
import { isCollectStep } from "../types/runtimeTypes";

const generateKountSessionId = (): string => crypto.randomUUID().replace(/-/g, "");

/**
 * @description Get Kount host from window properties passed in from view-main template.
 *              This avoids env specific logic in jslib.
 */
const getKountHost = (): string => {
  const { host } = window.KOUNT || {};

  if (!host || !host.match(/^(ssl|tst)\.kaptcha\.com$/)) {
    throw new Error("Kount environment not set properly");
  }

  return host;
};

/**
 * @description Initializes Kount device data collection and adds Kount session ID to metadata.
 *
 */
export const useKount = (verificationService: VerificationService) => {
  const kountEnabled = useFlag("kount-device-data") === "kount-enabled";
  const { currentStep } = verificationService?.verificationResponse || {};

  React.useEffect(() => {
    if (!kountEnabled || !verificationService?.viewModel || !isCollectStep(currentStep)) {
      return;
    }

    try {
      let sessionId = verificationService.viewModel.metadata?.kSessionId as string;

      if (!sessionId) {
        sessionId = generateKountSessionId();
        const newViewModel = produce(verificationService.viewModel, (draft) => {
          if (draft.metadata == null) {
            draft.metadata = {};
          }
          draft.metadata.kSessionId = sessionId!;
        });
        verificationService.updateViewModel(newViewModel);
      }

      const existingScript = document.querySelector<HTMLScriptElement>("script[data-kount-ddc]");
      if (existingScript) {
        if (existingScript.dataset.kountDdc === sessionId) {
          // Correct script already exists
          return;
        }
        // A script with a different session ID exists, remove it.
        existingScript.remove();
      }

      const script = document.createElement("script");
      const host = getKountHost();
      script.src = `https://${host}/collect/sdk?m=${import.meta.env.VITE_KOUNT_CLIENT_ID}&s=${sessionId}`;
      script.async = true;
      script.setAttribute("data-kount-ddc", sessionId);

      document.body.appendChild(script);
    } catch (error) {
      logger.error(error, "Failed to load Kount SDK");
    }
  }, [verificationService, kountEnabled]);
};
